import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { usePrimaryDevice } from './usePrimaryDevice';
import { getDeviceId } from '../lib/deviceStateManager';

// ALLTID mörk gradient som default (dark mode hero)
const DEFAULT_GRADIENT = {
  from: '#1a1a1a',
  via: '#2d2d2d',
  to: '#404040'
};

const CACHE_KEY_PREFIX = 'shop_banner_gradient_';

interface ShopBannerConfig {
  gradientFrom: string;
  gradientVia: string;
  gradientTo: string;
  deviceId?: string;
}

/**
 * Get cached gradient config from localStorage for a specific device
 */
function getCachedConfig(forDeviceId: string | null): ShopBannerConfig | null {
  if (!forDeviceId) return null;
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${forDeviceId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.gradientFrom && parsed.gradientVia && parsed.gradientTo) {
        return { ...parsed, deviceId: forDeviceId };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function getDefaultGradient() {
  return {
    gradientFrom: DEFAULT_GRADIENT.from,
    gradientVia: DEFAULT_GRADIENT.via,
    gradientTo: DEFAULT_GRADIENT.to
  };
}

export function useShopBannerConfig(deviceId?: string) {
  const { primaryDevice } = usePrimaryDevice();
  
  // CRITICAL FIX: Use cached deviceId from storage as INSTANT fallback
  // This ensures gradient loads immediately while we wait for DB query
  const cachedDeviceId = getDeviceId();
  
  // Priority: explicit prop > primaryDevice from DB > cached from storage
  const effectiveDeviceId = deviceId || primaryDevice?.id || cachedDeviceId;
  
  // INSTANT: Read cache synchronously on first render using device-specific key
  const [config, setConfig] = useState<ShopBannerConfig | null>(() => {
    const initialDeviceId = deviceId || cachedDeviceId;
    const cached = getCachedConfig(initialDeviceId);
    
    if (cached) {
      console.log('[useShopBannerConfig] ⚡ Instant cache hit for device:', initialDeviceId);
      return cached;
    }
    return null;
  });
  const [loading, setLoading] = useState(() => {
    const initialDeviceId = deviceId || cachedDeviceId;
    const cached = getCachedConfig(initialDeviceId);
    return !cached;
  });

  useEffect(() => {
    async function loadConfig() {
      // STEP 1: If we already have config from sync init, skip loading
      if (config && config.deviceId === effectiveDeviceId) {
        setLoading(false);
        return;
      }
      
      // STEP 2: Check device-specific cache
      const cached = getCachedConfig(effectiveDeviceId);
      if (cached) {
        console.log('[useShopBannerConfig] Using cached config for device:', effectiveDeviceId);
        setConfig(cached);
        setLoading(false);
        return;
      }

      // STEP 3: Fetch via RPC (bypasses RLS)
      if (!effectiveDeviceId) {
        setLoading(false);
        return;
      }

      try {
        console.log('[useShopBannerConfig] Fetching gradient via RPC for device:', effectiveDeviceId);
        
        const { data, error } = await supabase.rpc('get_device_gradient_config', {
          p_device_id: effectiveDeviceId
        });

        if (error) {
          console.warn('[useShopBannerConfig] RPC error:', error);
          setLoading(false);
          return;
        }

        if (data?.gradientFrom) {
          const newConfig = {
            gradientFrom: data.gradientFrom,
            gradientVia: data.gradientVia,
            gradientTo: data.gradientTo,
            deviceId: effectiveDeviceId
          };
          console.log('[useShopBannerConfig] ✅ Fetched gradient:', newConfig);
          setConfig(newConfig);
          
          // Cache with device-specific key
          try {
            localStorage.setItem(`${CACHE_KEY_PREFIX}${effectiveDeviceId}`, JSON.stringify(newConfig));
          } catch {
            // Ignore storage errors
          }
        }
      } catch (error) {
        console.error('[useShopBannerConfig] Error loading config:', error);
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, [effectiveDeviceId]);

  // Use org config if available, otherwise ALWAYS use dark gradient
  const finalConfig = config || getDefaultGradient();

  return { ...finalConfig, loading };
}
